import { Button, ButtonProps, Text, useDisclosure } from "@chakra-ui/react";
import moment from "moment";
import { FC } from "react";

import { RevokeIcon } from "@nens/ranalib";
import { ColumnDefinition, RanaModal, RanaTable } from "@nens/ranalib";
import { removeNullUndefined } from "@nens/shared-utils";

import { APP_BASE_HREF } from "../../App";
import { useRevisionSimulations, useThreediModelUsages } from "../../queries/simulations";

interface ChooseSimulationButtonProps extends ButtonProps {
  modelId?: number; // use *either* modelId or revisionId, depending on what you need
  revisionId?: number;
}

type RowData = {
  uuid: string;
  name: string;
  date: string | null;
};

const ChooseSimulationButton: FC<ChooseSimulationButtonProps> = ({
  modelId,
  revisionId,
  ...rest
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const modelsResponse = useThreediModelUsages(isOpen && modelId ? modelId : null);
  const revisionsResponse = useRevisionSimulations(isOpen && revisionId ? revisionId : null);

  const getData = (): RowData[] => {
    if (modelId && modelsResponse.isSuccess) {
      return removeNullUndefined(
        modelsResponse.data.results.map((usage) =>
          usage.simulation
            ? {
                uuid: "" + usage.simulation.id,
                name: usage.simulation.name,
                date: usage.started ? moment(usage.started).fromNow() : null,
              }
            : null
        )
      );
    }

    if (revisionId && revisionsResponse.isSuccess) {
      return revisionsResponse.data.results.map((revision) => ({
        uuid: "" + revision.id,
        name: revision.name,
        date: revision.created ?? null,
      }));
    }

    return [];
  };

  const data = getData();

  const columnDefinitions: ColumnDefinition[] = [
    {
      title: "Name",
      width: "table_cell_xs",
    },
    {
      title: "Created",
      width: "table_cell_xs",
    },
  ];

  return (
    <>
      <Button variant="secondary" leftIcon={<RevokeIcon boxSize={5} />} onClick={onOpen} {...rest}>
        Previous simulations with this {modelId ? "model" : "revision"}
      </Button>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar={"Choose a simulation"}
        maxW="400px"
        buttons={[
          <Button variant="primary" key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <RanaTable
          width={800}
          columnDefinitions={columnDefinitions}
          tableDataIds={data.map((row) => row.uuid)}
          isLoading={!revisionsResponse.isSuccess && !modelsResponse.isSuccess}
          emptyElement={
            <RanaTable.Empty>
              There is no data to be shown with the current filters.
            </RanaTable.Empty>
          }
          sx={{
            // vertical alignment for the icons and checkboxes
            "& p": {
              marginBottom: 0,
            },
            "& label": {
              marginBottom: 0,
            },
          }}
        >
          <RanaTable.Container>
            <RanaTable.Header />
            <RanaTable.Body>
              {data.map((row, index) => (
                <RanaTable.Row
                  to={`${APP_BASE_HREF}/simulations/${row.uuid}`}
                  onClick={onClose}
                  key={`${row.uuid}${index}`}
                  rowId={row.uuid}
                >
                  <RanaTable.Cell title={row.name}>{row.name}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Text>{row.date}</Text>
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
      </RanaModal>
    </>
  );
};

export default ChooseSimulationButton;
